import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { EditDrawer } from '../pages/AdminTeam';
import { SIDEBAR_CONFIGS } from './AdminSidebar';
import { useStaffRole } from '../hooks/useStaffRole';
import { NAV_LINKS, visibleNavLinks, shellBadgeFeeds } from '../constants/staffNav';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ── 🚨 THESE TABS HAD NO ROLE FILTER AT ALL ──────────────────────────────────
//
// Every role saw all seven. A Partner Success rep saw Dashboard, Borrowers,
// Marketing, Finance and Schedule — five tabs onto sections the server refuses
// them — and clicking any of them produced the error box they reported.
//
// 🔑 VISIBILITY IS DERIVED FROM THE TARGET, through the same maySeePath() the
// router uses, so a tab cannot offer a page its own section hides and adding a
// rule to a section removes its tab without anybody editing this file.
// Reports moved into the Borrowers left-nav (see AdminSidebar SIDEBAR_CONFIGS);
// Surveys now lives under the Marketing section. Both routes still exist.

export default function AdminNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  // Most-specific NAV_LINK wins, so "Tickets" (/admin/dashboard/customer-service)
  // highlights on the hub path and "Dashboard" (/admin/dashboard) does not.
  const navActive = (to) => {
    const matches = NAV_LINKS.filter((l) => location.pathname === l.to || location.pathname.startsWith(l.to + '/'));
    const best = matches.reduce((a, b) => (b.to.length > (a ? a.to.length : 0) ? b : a), null);
    return best ? best.to === to : false;
  };
  const { role } = useStaffRole();
  // 🔑 THE TABS THIS ROLE MAY ACTUALLY USE. Both the desktop row and the mobile
  // drawer render this, so neither can offer a section the other hides.
  const myLinks = visibleNavLinks(role);
  const token = localStorage.getItem('doorly_admin_token');
  const [adminUser, setAdminUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('doorly_admin_user') || '{}'); } catch { return {}; }
  });

  useEffect(() => {
    const handler = () => {
      try { setAdminUser(JSON.parse(localStorage.getItem('doorly_admin_user') || '{}')); } catch {}
    };
    window.addEventListener('doorly:profileUpdated', handler);
    return () => window.removeEventListener('doorly:profileUpdated', handler);
  }, []);

  // Refresh profile from DB on mount to pick up latest profile_photo
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const u = res.data.user;
        if (!u) return;
        setAdminUser((prev) => {
          const updated = {
            ...prev,
            firstName: u.first_name,
            lastName: u.last_name,
            email: u.email,
            job_title: u.job_title,
            user_type: u.user_type,
            profile_photo: u.profile_photo || null,
          };
          localStorage.setItem('doorly_admin_user', JSON.stringify(updated));
          return updated;
        });
      })
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [mobileBadges, setMobileBadges] = useState({ messages: 0, alerts: 0, borrowerAlerts: 0, partnerAlerts: 0, marketAlerts: 0, showingRequests: 0, offerRequests: 0 });

  // Listen for the avatar-driven events that AdminSidebar now fires.
  // The desktop avatar moved into the sidebar above "Doorly Admin Portal";
  // EditProfile + Logout-confirm modals stay rendered here (the mobile
  // drawer below also opens them) and are surfaced via window events.
  useEffect(() => {
    const onEdit   = () => setShowEditProfile(true);
    const onLogout = () => setShowLogoutConfirm(true);
    window.addEventListener('doorly:openEditProfile', onEdit);
    window.addEventListener('doorly:openLogoutConfirm', onLogout);
    return () => {
      window.removeEventListener('doorly:openEditProfile', onEdit);
      window.removeEventListener('doorly:openLogoutConfirm', onLogout);
    };
  }, []);

  // Close the mobile drawer whenever the route changes (click-to-navigate UX)
  useEffect(() => { setShowMobileMenu(false); }, [location.pathname, location.search]);

  // Lock body scroll while the mobile drawer is open, and close on Escape.
  useEffect(() => {
    if (!showMobileMenu) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setShowMobileMenu(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [showMobileMenu]);

  // Fetch badges so the mobile sub-nav shows the same counts as the desktop
  // sidebar. Identical endpoints + polling cadence — and the same role gate:
  // a feed for an area this role's nav does not render is not called
  // (constants/staffNav shellBadgeFeeds; see AdminSidebar).
  useEffect(() => {
    if (!token) return;
    const feeds = shellBadgeFeeds(role);
    const skip = (data) => Promise.resolve({ data });
    const fetchBadges = async () => {
      try {
        const [masterRes, borrowerRes, partnerRes, requestCountsRes, marketRes] = await Promise.all([
          // 🚨 THIS ONE HAD NO .catch(), the exact bug AdminSidebar's note on the
          // same call describes: Promise.all rejects on the first refusal, the
          // outer catch swallows it, and EVERY mobile badge stayed zero for any
          // role this endpoint refused. Caught like its siblings, so one refused
          // call can only ever zero its own badge.
          axios.get(`${API}/api/admin/alerts/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
            .catch(() => ({ data: {} })),
          feeds.borrowerAlerts
            ? axios.get(`${API}/api/admin/alerts?category=borrower&status=unread&limit=0`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { unread_count: 0 } }))
            : skip({ unread_count: 0 }),
          feeds.partnerAlerts
            ? axios.get(`${API}/api/admin/alerts?category=partner&status=unread&limit=0`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { unread_count: 0 } }))
            : skip({ unread_count: 0 }),
          feeds.requestCounts
            ? axios.get(`${API}/api/admin/borrowers/request-counts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { showing_requests: 0, offer_requests: 0 } }))
            : skip({ showing_requests: 0, offer_requests: 0 }),
          feeds.marketAlerts
            ? axios.get(`${API}/api/admin/market/alerts/unread-count`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { count: 0 } }))
            : skip({ count: 0 }),
        ]);
        setMobileBadges({
          messages:        masterRes.data.messagesCount || 0,
          alerts:          masterRes.data.alertsCount || 0,
          borrowerAlerts:  borrowerRes.data.unread_count || 0,
          partnerAlerts:   partnerRes.data.unread_count || 0,
          showingRequests: requestCountsRes.data.showing_requests || 0,
          offerRequests:   requestCountsRes.data.offer_requests || 0,
          marketAlerts:    marketRes.data.count || 0,
        });
      } catch {}
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => clearInterval(interval);
  }, [token, role]);

  const handleLogout = () => {
    localStorage.removeItem('doorly_admin_token');
    localStorage.removeItem('doorly_admin_user');
    localStorage.removeItem('doorly_admin_force_pw');
    navigate('/admin/login');
  };

  const navLinkStyle = ({ isActive }) => ({
    fontSize: '14px',
    fontWeight: isActive ? '600' : '500',
    color: isActive ? '#3a6ad9' : '#374151',
    textDecoration: 'none',
    padding: '6px 12px',
    borderRadius: '8px',
    backgroundColor: isActive ? '#eff4ff' : 'transparent',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  });

  // Resolve the contextual sidebar config for the current route so the mobile
  // drawer can render the same sub-nav as the desktop sidebar. No match means
  // no sub-nav section is rendered.
  const sectionConfig = SIDEBAR_CONFIGS.find((c) => c.match(location.pathname));

  // Mobile sub-nav helpers — mirror the desktop sidebar's role filtering and
  // nested `children` groups. The drawer previously assumed every item had a
  // `path`, so the "Active Modules"/"Reports" group rows (which carry
  // `children` and NO path) crashed it on `item.path.includes(...)` of
  // undefined — that was the "Something went wrong" the hamburger hit on
  // Borrowers-section routes after the nav restructure.
  const visibleItems = (items) =>
    (items || []).filter((it) => !(it.hideForRoles || []).includes(role));

  const renderNavLeaf = (item, key) => {
    const fullPath = location.pathname + location.search;
    const itemPath = item.path;
    const active =
      (itemPath.includes('?') && itemPath === fullPath) ||
      (itemPath.includes('?view=all') && location.pathname === itemPath.split('?')[0] && !location.search) ||
      (itemPath === location.pathname && !itemPath.includes('?')) ||
      (!item.exact && itemPath !== '/admin/dashboard' && location.pathname.startsWith(itemPath + '/'));
    const badgeCount = item.badgeKey ? (mobileBadges[item.badgeKey] || 0) : 0;
    return (
      <button
        key={key}
        onClick={() => { navigate(item.path); setShowMobileMenu(false); }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          width: '100%', padding: '9px 12px', borderRadius: 8,
          background: active ? '#eff4ff' : 'transparent',
          border: 'none', cursor: 'pointer', textAlign: 'left',
          fontSize: 13, fontWeight: active ? 700 : 500,
          color: active ? '#3a6ad9' : '#374151', marginBottom: 2,
        }}>
        <span>{item.label}</span>
        {badgeCount > 0 && (
          <span style={{ backgroundColor: '#ff082f', color: '#fff', borderRadius: 9999, fontSize: 10, fontWeight: 700, minWidth: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        // The iOS status-bar inset is applied here as paddingTop (env() in
        // padding is reliable); the inner bar below is a fixed 64px so the
        // logo/hamburger always center in the visible band below the status
        // bar, and the white background fills the inset area too. Avoids
        // env() inside calc() for `height`, which is flaky on iOS WebKit and
        // was leaving the navbar ~64px while content reserved 64px+inset
        // (the grey gap), and avoids the `padding` shorthand clobbering
        // paddingTop (which mis-centered the logo too high).
        paddingTop: 'env(safe-area-inset-top, 0px)',
        backgroundColor: '#ffffff', borderBottom: '1px solid #e5e7eb',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      }}>
        <div style={{ height: '64px', display: 'flex', alignItems: 'center', paddingLeft: '16px', paddingRight: '16px', gap: '16px' }}>
        {/* Logo + Desk label — leading (left) position */}
        <NavLink to="/admin/dashboard" style={{ textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src="/doorly_logo_black.png" alt="Doorly" style={{ height: '32px', width: 'auto' }} />
          <span className="admin-desk-badge" style={{
            fontSize: '11px', fontWeight: '700', color: '#3a6ad9',
            backgroundColor: '#eff6ff', padding: '2px 7px', borderRadius: '5px',
            letterSpacing: '0.04em', textTransform: 'uppercase',
          }}>Desk</span>
        </NavLink>

        {/* Desktop Nav Links — Team / user-management is intentionally NOT
            in the top nav. Admins reach /admin/settings/users via the
            avatar dropdown only, so the page stays out of the everyday
            navigation surface for non-admin staff (and for admins when
            they don't need it). */}
        <div className="admin-nav-links" style={{ display: 'flex', gap: '4px', flex: 1, justifyContent: 'center' }}>
          {myLinks.map((link) => (
            <NavLink key={link.to} to={link.to} style={() => navLinkStyle({ isActive: navActive(link.to) })}>{link.label}</NavLink>
          ))}
        </div>

        {/* Right side: DoorlyMarket CTA + account.
            DoorlyMarket hides on mobile (accessible via the drawer instead). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: 'auto' }}>
          <button
            className="admin-market-cta"
            onClick={() => navigate('/admin/market')}
            style={{
              padding: '7px 16px', background: '#3a6ad9', color: '#ffffff',
              border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontSize: '13px', fontWeight: '600', display: 'inline-flex', alignItems: 'center',
            }}
          >
            DoorlyMarket
          </button>

          {/* Mobile hamburger — trailing (right) position; logo stays left.
              Hidden on desktop (DoorlyMarket shows instead). */}
          <button
            className="admin-hamburger"
            onClick={() => setShowMobileMenu((v) => !v)}
            aria-label="Open navigation menu"
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}
          >
            <svg width="26" height="26" fill="none" viewBox="0 0 24 24" stroke="#111827" strokeWidth="2.25">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Profile avatar moved to AdminSidebar's footer (above
              "Doorly Admin Portal"). Modals + mobile drawer entries
              still live in this file. */}
        </div>
        </div>
      </nav>

      {/* Mobile slide-in drawer — full height, includes sub-nav for current section */}
      {showMobileMenu && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setShowMobileMenu(false)}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(17,24,39,0.45)', zIndex: 1090, animation: 'adminMobileMenuFade 0.15s ease' }}
          />
          {/* Drawer */}
          <div style={{
            position: 'fixed', top: 0, left: 0, bottom: 0,
            width: '280px', maxWidth: '85vw',
            backgroundColor: '#ffffff', boxShadow: '2px 0 20px rgba(0,0,0,0.15)',
            zIndex: 1095, display: 'flex', flexDirection: 'column',
            overflowY: 'auto',
            animation: 'adminMobileMenuSlide 0.2s ease',
          }}>
            {/* Drawer header */}
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src="/doorly_logo_black.png" alt="Doorly" style={{ height: '28px' }} />
              </div>
              <button onClick={() => setShowMobileMenu(false)} aria-label="Close menu" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#6b7280', fontSize: '22px', lineHeight: 1 }}>×</button>
            </div>

            {/* User chip */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: 36, height: 36, borderRadius: '9999px', overflow: 'hidden', flexShrink: 0, backgroundColor: '#3a6ad9' }}>
                {adminUser.profile_photo ? (
                  <img
                    src={adminUser.profile_photo.startsWith('data:') || adminUser.profile_photo.startsWith('http') ? adminUser.profile_photo : '/assets/doorly_icon_default.png'}
                    alt="Profile" style={{ width: 36, height: 36, objectFit: 'cover' }}
                    onError={(e) => { e.target.src = '/assets/doorly_icon_default.png'; }}
                  />
                ) : (
                  <img src="/assets/doorly_icon_default.png" alt="Doorly" style={{ width: 36, height: 36, objectFit: 'cover' }} />
                )}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{adminUser.firstName} {adminUser.lastName}</div>
                <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{adminUser.email}</div>
              </div>
            </div>

            {/* Primary nav */}
            <div style={{ padding: '8px 12px' }}>
              {myLinks.map((link) => (
                <NavLink
                  key={link.to} to={link.to}
                  style={() => { const isActive = navActive(link.to); return ({
                    display: 'block', padding: '10px 12px', borderRadius: '8px',
                    color: isActive ? '#3a6ad9' : '#111827', fontWeight: isActive ? '700' : '500',
                    fontSize: '14px', textDecoration: 'none', marginBottom: '2px',
                    backgroundColor: isActive ? '#eff4ff' : 'transparent',
                  }); }}
                >{link.label}</NavLink>
              ))}
            </div>

            {/* Contextual sub-nav for the current section */}
            {sectionConfig && (
              <div style={{ borderTop: '1px solid #f3f4f6', padding: '10px 12px' }}>
                <div style={{ padding: '4px 12px 8px', fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  This section
                </div>
                {visibleItems(sectionConfig.items).map((item, i) => {
                  if (item.type === 'divider') {
                    return <div key={`d-${i}`} style={{ height: 1, backgroundColor: '#f3f4f6', margin: '6px 10px' }} />;
                  }
                  // Nested group (e.g. "Active Modules", "Reports"): no path of
                  // its own — render its label as a sub-header and its
                  // role-filtered children as leaves.
                  if (item.children) {
                    const kids = visibleItems(item.children);
                    if (!kids.length) return null;
                    return (
                      <div key={`g-${i}`}>
                        <div style={{ padding: '8px 12px 4px', fontSize: 11, fontWeight: 700, color: '#6b7280' }}>
                          {item.label}
                        </div>
                        <div style={{ paddingLeft: 8 }}>
                          {kids.map((kid, j) => renderNavLeaf(kid, `g-${i}-${j}`))}
                        </div>
                      </div>
                    );
                  }
                  if (!item.path) return null; // safety: skip any other non-leaf shape
                  return renderNavLeaf(item, `${item.label}-${i}`);
                })}
              </div>
            )}

            {/* DoorlyMarket + account actions at the bottom */}
            <div style={{ marginTop: 'auto', borderTop: '1px solid #f3f4f6', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button
                onClick={() => { navigate('/admin/market'); setShowMobileMenu(false); }}
                style={{ padding: '10px 12px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, textAlign: 'left', marginBottom: 6 }}
              >
                DoorlyMarket →
              </button>
              {[
                { label: 'Edit Profile', icon: '✏️', action: () => { setShowEditProfile(true); setShowMobileMenu(false); } },
                { label: 'Settings',     icon: '👤', action: () => { navigate('/admin/settings/users'); setShowMobileMenu(false); } },
                { label: 'Log Out',      icon: '🚪', action: () => { setShowLogoutConfirm(true); setShowMobileMenu(false); }, danger: true },
              ].map((item) => (
                <button key={item.label} onClick={item.action}
                  style={{ width: '100%', textAlign: 'left', padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: 8, fontSize: 13, fontWeight: 500, color: item.danger ? '#ff082f' : '#374151', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>{item.icon}</span>{item.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Logout Confirm Modal */}
      {showLogoutConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '28px', width: '340px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontWeight: '700', fontSize: '16px', color: '#111827', marginBottom: '8px' }}>Sign out?</h3>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>You will be redirected to the login page.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowLogoutConfirm(false)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Cancel</button>
              <button onClick={handleLogout} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#ff082f', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>Sign Out</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Drawer */}
      {showEditProfile && (
        <EditDrawer
          member={{
            id: adminUser.id,
            first_name: adminUser.firstName || '',
            last_name: adminUser.lastName || '',
            email: adminUser.email || '',
            user_type: adminUser.user_type || 'Admin',
            job_title: adminUser.job_title || '',
            profile_photo: adminUser.profile_photo || null,
          }}
          currentUserType={adminUser.user_type || 'Admin'}
          onClose={() => setShowEditProfile(false)}
          onSave={(updated) => {
            const newUser = {
              ...adminUser,
              firstName: updated.first_name,
              lastName: updated.last_name,
              email: updated.email,
              job_title: updated.job_title,
              profile_photo: updated.profile_photo || null,
            };
            localStorage.setItem('doorly_admin_user', JSON.stringify(newUser));
            setAdminUser(newUser);
            window.dispatchEvent(new Event('doorly:profileUpdated'));
            setShowEditProfile(false);
          }}
          onError={() => {}}
        />
      )}

      {/* Responsive CSS */}
      <style>{`
        @keyframes adminMobileMenuFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes adminMobileMenuSlide { from { transform: translateX(-20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @media (max-width: 768px) {
          .admin-nav-links { display: none !important; }
          .admin-hamburger { display: inline-flex !important; align-items: center; justify-content: center; }
          .admin-market-cta { display: none !important; }
        }
      `}</style>
    </>
  );
}
